import React, {Fragment, useCallback, useEffect, useMemo, useState} from "react";
import {
    Button,
    Card,
    CardFooter,
    CardBody,
    CardHeader,
    Col,
    Container,
    FormGroup,
    Input,
    Label,
    Row,
    Table,
} from "reactstrap";
import {useHistory} from "react-router";
import SimpleHeader from "components/Headers/SimpleHeader.js";
import sharingPloggingService from "./service/sharingPlogging.service";
import PaginationWrapper from "../components/pagination";
import {numberWithCommas, paginate, telWithCommas} from "../../../utils";

const getErrorMessage = (e) => {
    return e?.response?.data?.message || e?.response?.data?.resultMsg || e?.message || "처리 중 오류가 발생했습니다.";
};

const formatDateTime = (value) => {
    if (!value) {
        return "-";
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${year}.${month}.${day} ${hours}:${minutes}`;
};

const formatUsageTime = (value) => {
    if (!value) {
        return "0시간";
    }
    const [hours = "0"] = String(value).split(":");
    return `${Number(hours) || 0}시간`;
};

const getPloggingValue = (item, key) => {
    if (!item) {
        return undefined;
    }
    if (item.plogging && item.plogging[key] !== undefined && item.plogging[key] !== null) {
        return item.plogging[key];
    }
    return item[key];
};

function SharedPloggingDetail({match}) {
    const history = useHistory();
    const sharingPloggingId = match.params.id;
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [detail, setDetail] = useState(null);
    const [paymentPage, setPaymentPage] = useState(1);
    const [usagePage, setUsagePage] = useState(1);
    const [form, setForm] = useState({
        centerLoginId: "",
        price: "",
        priority: "0",
        active: true,
    });
    const [thumbnailFile, setThumbnailFile] = useState(null);
    const [imageFile, setImageFile] = useState(null);

    const thumbnailPreview = useMemo(
        () => (thumbnailFile ? URL.createObjectURL(thumbnailFile) : detail?.thumbnail || ""),
        [thumbnailFile, detail],
    );
    const imagePreview = useMemo(
        () => (imageFile ? URL.createObjectURL(imageFile) : detail?.image || ""),
        [imageFile, detail],
    );

    const fetchDetail = useCallback(async () => {
        try {
            setLoading(true);
            const res = await sharingPloggingService.detail(sharingPloggingId);
            if (res.code === 200) {
                setDetail({
                    ...res.data,
                    payments: Array.isArray(res.data?.payments) ? res.data.payments : [],
                    usages: Array.isArray(res.data?.usages) ? res.data.usages : [],
                });
                setPaymentPage(1);
                setUsagePage(1);
                setForm({
                    centerLoginId: res.data.centerLoginId || "",
                    price: String(res.data.price || ""),
                    priority: String(res.data.priority || 0),
                    active: !!res.data.active,
                });
            }
        } catch (e) {
            alert(getErrorMessage(e));
            history.push("/admin/sharingPlogging");
        } finally {
            setLoading(false);
        }
    }, [history, sharingPloggingId]);

    useEffect(() => {
        fetchDetail();
    }, [fetchDetail]);

    const handleChange = (key, value) => {
        setForm(prev => ({
            ...prev,
            [key]: value,
        }));
    };

    const handleSave = async () => {
        if (!form.centerLoginId || !form.price) {
            alert("봉사센터 로그인 아이디와 금액을 입력해주세요.");
            return;
        }

        try {
            setSaving(true);
            await sharingPloggingService.update(sharingPloggingId, {
                centerLoginId: form.centerLoginId.trim(),
                price: Number(form.price),
                priority: Number(form.priority || 0),
                active: !!form.active,
            });

            if (thumbnailFile) {
                const thumbnailForm = new FormData();
                thumbnailForm.append("image", thumbnailFile);
                await sharingPloggingService.fileRegister(sharingPloggingId, thumbnailForm);
            }

            if (imageFile) {
                const imageForm = new FormData();
                imageForm.append("image", imageFile);
                await sharingPloggingService.imageRegister(sharingPloggingId, imageForm);
            }

            alert("정상적으로 수정되었습니다.");
            setThumbnailFile(null);
            setImageFile(null);
            await fetchDetail();
        } catch (e) {
            alert(getErrorMessage(e));
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm("삭제하시겠습니까?")) {
            return;
        }
        try {
            await sharingPloggingService.remove(sharingPloggingId);
            alert("정상적으로 삭제되었습니다.");
            history.push("/admin/sharingPlogging");
        } catch (e) {
            alert(getErrorMessage(e));
        }
    };

    const handlePaymentCancel = async (payId) => {
        if (!window.confirm("해당 결제를 취소하시겠습니까?")) {
            return;
        }
        try {
            await sharingPloggingService.cancelPayment(payId);
            alert("결제가 취소되었습니다.");
            await fetchDetail();
        } catch (e) {
            alert(getErrorMessage(e));
        }
    };

    if (loading || !detail) {
        return null;
    }

    const payments = Array.isArray(detail.payments) ? detail.payments : [];
    const usages = Array.isArray(detail.usages) ? detail.usages : [];
    const pagedPayments = paginate(payments, paymentPage, 10);
    const pagedUsages = paginate(usages, usagePage, 10);

    return (
        <>
            <SimpleHeader name="Tables" parentName="Tables"/>
            <Container className="mt--6" fluid>
                <Row>
                    <div className="col">
                        <Card className="mb-4">
                            <CardHeader className="border-0">
                                <Row>
                                    <Col xs="6">
                                        <h3 className="mt-2">플로깅 정보 확인 및 수정</h3>
                                    </Col>
                                    <Col className="text-right">
                                        <Button type="button" className="mr-2" color="default" disabled={saving} onClick={handleSave}>
                                            {saving ? "저장중" : "수정하기"}
                                        </Button>
                                        <Button type="button" color="danger" onClick={handleDelete}>
                                            삭제하기
                                        </Button>
                                    </Col>
                                </Row>
                            </CardHeader>
                            <CardBody>
                                <Row>
                                    <Col md="12">
                                        <h4 className="mb-3">수정 가능 정보</h4>
                                    </Col>
                                    <Col md="3">
                                        <FormGroup>
                                            <Label>봉사센터 로그인 아이디</Label>
                                            <Input
                                                value={form.centerLoginId}
                                                onChange={(e) => handleChange("centerLoginId", e.target.value)}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="3">
                                        <FormGroup>
                                            <Label>1인 기준 월 기부 전달 금액</Label>
                                            <Input
                                                value={form.price}
                                                onChange={(e) => handleChange("price", e.target.value.replace(/[^0-9]/g, ""))}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="3">
                                        <FormGroup>
                                            <Label>우선순위</Label>
                                            <Input
                                                value={form.priority}
                                                onChange={(e) => handleChange("priority", e.target.value.replace(/[^0-9]/g, ""))}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="3">
                                        <FormGroup>
                                            <Label>활성여부</Label>
                                            <Input
                                                type="select"
                                                value={form.active ? "true" : "false"}
                                                onChange={(e) => handleChange("active", e.target.value === "true")}
                                            >
                                                <option value="true">활성</option>
                                                <option value="false">비활성</option>
                                            </Input>
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md="3">
                                        <FormGroup>
                                            <Label>봉사센터명</Label>
                                            <Input value={detail.center || ""} disabled/>
                                        </FormGroup>
                                    </Col>
                                    <Col md="3">
                                        <FormGroup>
                                            <Label>총 결제 금액</Label>
                                            <Input value={`${numberWithCommas(detail.payAmount)}원`} disabled/>
                                        </FormGroup>
                                    </Col>
                                    <Col md="3">
                                        <FormGroup>
                                            <Label>총 이용시간</Label>
                                            <Input value={formatUsageTime(detail.totalUsageTime)} disabled/>
                                        </FormGroup>
                                    </Col>
                                    <Col md="3">
                                        <FormGroup>
                                            <Label>총 이용 건수</Label>
                                            <Input value={detail.workingCount || 0} disabled/>
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>썸네일</Label>
                                            <Input type="file" onChange={(e) => setThumbnailFile(e.target.files?.[0] || null)}/>
                                            {thumbnailPreview && (
                                                <img
                                                    alt="thumbnail"
                                                    src={thumbnailPreview}
                                                    style={{width: 180, height: 180, objectFit: "cover", marginTop: 12}}
                                                />
                                            )}
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>상세 이미지</Label>
                                            <Input type="file" onChange={(e) => setImageFile(e.target.files?.[0] || null)}/>
                                            {imagePreview && (
                                                <img
                                                    alt="detail"
                                                    src={imagePreview}
                                                    style={{width: 180, height: 180, objectFit: "cover", marginTop: 12}}
                                                />
                                            )}
                                        </FormGroup>
                                    </Col>
                                </Row>
                            </CardBody>
                        </Card>

                        <Card className="mb-4">
                            <CardHeader className="border-0">
                                <h3 className="mb-0">결제내역 리스트</h3>
                            </CardHeader>
                            <Table className="align-items-center table-flush" responsive striped>
                                <thead className="thead-light">
                                <tr>
                                    <th className="text-center">결제일시</th>
                                    <th className="text-center">상태</th>
                                    <th className="text-center">회원</th>
                                    <th className="text-center">아이디</th>
                                    <th className="text-center">전화번호</th>
                                    <th className="text-center">결제금액</th>
                                    <th className="text-center">주문번호</th>
                                    <th className="text-center">관리</th>
                                </tr>
                                </thead>
                                <tbody>
                                {pagedPayments.map((item) => (
                                    <Fragment key={item.id}>
                                        <tr>
                                            <td className="text-center">{formatDateTime(item.paidAt)}</td>
                                            <td className="text-center">{item.status === "취소" ? "결제취소" : "결제완료"}</td>
                                            <td className="text-center">{item.user?.name || "-"}</td>
                                            <td className="text-center">{item.user?.loginId || "-"}</td>
                                            <td className="text-center">{item.user?.tel ? telWithCommas(item.user.tel) : "-"}</td>
                                            <td className="text-center">{numberWithCommas(item.amount)}원</td>
                                            <td className="text-center">{item.merchantUid}</td>
                                            <td className="text-center">
                                                {item.status === "완료" ? (
                                                    <Button
                                                        type="button"
                                                        color="danger"
                                                        size="sm"
                                                        onClick={() => handlePaymentCancel(item.id)}
                                                    >
                                                        결제 취소
                                                    </Button>
                                                ) : (
                                                    "-"
                                                )}
                                            </td>
                                        </tr>
                                    </Fragment>
                                ))}
                                {payments.length === 0 && (
                                    <tr>
                                        <td className="text-center" colSpan="8">결제내역이 없습니다.</td>
                                    </tr>
                                )}
                                </tbody>
                            </Table>
                            <CardFooter className="py-4">
                                <PaginationWrapper
                                    isActive={paymentPage}
                                    pageNumber={payments.length}
                                    callback={(page) => setPaymentPage(page)}
                                    itemPerPage={10}
                                />
                            </CardFooter>
                        </Card>

                        <Card>
                            <CardHeader className="border-0">
                                <h3 className="mb-0">이용 내역 리스트</h3>
                            </CardHeader>
                            <Table className="align-items-center table-flush" responsive striped>
                                <thead className="thead-light">
                                <tr>
                                    <th className="text-center">이용일시</th>
                                    <th className="text-center">회원</th>
                                    <th className="text-center">아이디</th>
                                    <th className="text-center">전화번호</th>
                                    <th className="text-center">이용시간</th>
                                    <th className="text-center">거리</th>
                                    <th className="text-center">주소</th>
                                    <th className="text-center">상세</th>
                                </tr>
                                </thead>
                                <tbody>
                                {pagedUsages.map((item) => (
                                    <Fragment key={item.id}>
                                        <tr>
                                            <td className="text-center">{formatDateTime(getPloggingValue(item, "createdAt"))}</td>
                                            <td className="text-center">{item.user?.name || "-"}</td>
                                            <td className="text-center">{item.user?.loginId || "-"}</td>
                                            <td className="text-center">{item.user?.tel ? telWithCommas(item.user.tel) : "-"}</td>
                                            <td className="text-center">{formatUsageTime(getPloggingValue(item, "time"))}</td>
                                            <td className="text-center">{getPloggingValue(item, "distance") ? `${getPloggingValue(item, "distance")}km` : "-"}</td>
                                            <td className="text-center">{getPloggingValue(item, "address") || "-"}</td>
                                            <td className="text-center">
                                                <Button
                                                    className="btn-outline-default"
                                                    size="sm"
                                                    onClick={() => history.push({
                                                        pathname: `/admin/sharedPloggingCalcMgmtPloggingDetail/${sharingPloggingId}/${encodeURIComponent(detail.center || "")}/${item.id}`,
                                                        state: {
                                                            item: {
                                                                ...item,
                                                                plogging: {
                                                                    ...(item.plogging || {}),
                                                                    createdAt: getPloggingValue(item, "createdAt"),
                                                                    startTime: getPloggingValue(item, "startTime"),
                                                                    endTime: getPloggingValue(item, "endTime"),
                                                                    time: getPloggingValue(item, "time"),
                                                                    distance: getPloggingValue(item, "distance"),
                                                                    address: getPloggingValue(item, "address"),
                                                                    addressDetail: getPloggingValue(item, "addressDetail"),
                                                                    data: getPloggingValue(item, "data"),
                                                                    paper: getPloggingValue(item, "paper"),
                                                                    can: getPloggingValue(item, "can"),
                                                                    styrofoam: getPloggingValue(item, "styrofoam"),
                                                                    pet: getPloggingValue(item, "pet"),
                                                                    vinyl: getPloggingValue(item, "vinyl"),
                                                                    plastic: getPloggingValue(item, "plastic"),
                                                                    glass: getPloggingValue(item, "glass"),
                                                                    landfill: getPloggingValue(item, "landfill"),
                                                                    cigarette: getPloggingValue(item, "cigarette"),
                                                                    co2: getPloggingValue(item, "co2"),
                                                                    stepCo2: getPloggingValue(item, "stepCo2"),
                                                                    totalCo2: getPloggingValue(item, "totalCo2"),
                                                                    ploggingImages: item?.plogging?.ploggingImages || item?.ploggingImages || [],
                                                                    check: getPloggingValue(item, "check"),
                                                                },
                                                                data: getPloggingValue(item, "data"),
                                                                address: getPloggingValue(item, "address"),
                                                            }
                                                        },
                                                    })}
                                                >
                                                    상세
                                                </Button>
                                            </td>
                                        </tr>
                                    </Fragment>
                                ))}
                                {usages.length === 0 && (
                                    <tr>
                                        <td className="text-center" colSpan="8">이용 내역이 없습니다.</td>
                                    </tr>
                                )}
                                </tbody>
                            </Table>
                            <CardFooter className="py-4">
                                <PaginationWrapper
                                    isActive={usagePage}
                                    pageNumber={usages.length}
                                    callback={(page) => setUsagePage(page)}
                                    itemPerPage={10}
                                />
                            </CardFooter>
                        </Card>
                    </div>
                </Row>
            </Container>
        </>
    );
}

export default SharedPloggingDetail;
